/**
 * WHAT A NAME IS, BEFORE YOU OPEN IT — the card under the pointer.
 *
 * Between "look at the field" and "open a name" there is the thing a reader does most: move the
 * pointer across the map and lean in. Theirs answers that with a small card naming the figure and
 * saying what it is — "Adam · Person · 4 corpora" — and by turning the cursor to a pointer over a
 * name and back to a grab over the dark (engine.js:641-650).
 *
 * Ours answered with nothing at all, and its cursor said `grab` everywhere: the map never once told
 * a reader that a name could be opened. That is the whole affordance of the thing.
 *
 * THE SUBTITLE IS THEIRS EXACTLY (engine.js:823) — the kind, then either the scriptures joined by
 * "&" or a count of them once there are more than two. Two names fit and read better than "2
 * corpora"; five do not fit at all.
 *
 * WHAT IS NOT PORTED, and deliberately: theirs shows "an unnamed light · not yet surveyed" for a
 * figure the reader has not walked to or seen. That is the frontier, and whether this map withholds
 * what it has already drawn is a question standing open with Ariel — see V8-PORT.md. Ours names
 * everything it draws, which is also what its far view now does. The hook is `known`, so the answer,
 * whichever way it goes, is one argument.
 */

/** What the card says. Two lines: the name, and what it is. */
export interface HoverCard {
  name: string;
  sub: string;
}

/** Above this many scriptures the card counts them instead of naming them — engine.js:825. */
export const NAME_UP_TO = 2;

export interface HoverNode {
  name: string;
  /** The scriptures naming this figure, already resolved to their display names. */
  corpora: readonly string[];
  /** What kind of thing it is, already in the reader's language. */
  kind: string;
}

/**
 * The card for a figure.
 *
 * `count` renders "4 corpora" in whichever language is on; `join` renders "Hebrew Bible & Quran".
 * Both are passed in because both are words, and every word in this port belongs to the dictionary
 * rather than to a layer.
 */
export function hoverCard(
  node: HoverNode,
  { count, join, nameUpTo = NAME_UP_TO }: {
    count: (n: number) => string;
    join: (names: readonly string[]) => string;
    nameUpTo?: number;
  },
): HoverCard {
  const listed = node.corpora.length > nameUpTo ? count(node.corpora.length) : join(node.corpora);
  return { name: node.name, sub: listed === '' ? node.kind : `${node.kind} · ${listed}` };
}
